import React, { useState, useEffect, useCallback } from "react";
import ArcadeGame from "../ArcadeCard";
import Navigation from "../../App/Navigation";
import ProgressBar from "../../ProgressiveBar";
import { ROUTES } from "../../../consts";
import { useTrackContext } from "../../../hooks/useTrackContext";
import { MinusIcon, PlusIcon, ShoppingCartIcon } from "@heroicons/react/solid";
import NewsletterPopup from "../../NewsletterPopup";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../../config";
import { ADD_TO_CART_LINES, GET_PRODUCT_BY_HANDLE } from "../../../graphqlQueries";
import { ICartItemHeadless, IGProduct, IGVariant } from "../../../types";
import { ImSpinner6 } from "react-icons/im";
import { getAdminIdFromStoreFrontId } from "../../../helpers/shopify";
import { useLocation } from "react-router-dom";
import MiniCart from "../../Cart/MiniCart";
import { query } from "../../../lib";
import { LoadingAnimationURL } from "../../../consts/assetUrls";

const ArcadeCard: React.FC = () => {
  const [product, setProduct] = useState<IGProduct | null>(null);
  const [isProductAdding, setIsProductAdding] = useState<boolean>(false);
  const [isBookingNow, setIsBookingNow] = useState<boolean>(false);
  const [nextRoute, setNextRoute] = useState<string>("#");

  const {
    arcadeSelection: {
      arcadeGameChoose: { selectedQuantity, selectedVariantId, isProductOutOfStock },
    },
    dispatch,
    productRenderedLocation,
    headlessCartInfo,
  } = useTrackContext();

  const location = useLocation();

  useEffect(() => {
    if (productRenderedLocation !== "external") {
      window.scrollTo(0, 0);
    }
    if (location.state && location.state?.previous.includes("main-track")) {
      setNextRoute("/" + ROUTES.MAIN_TRACK.ReviewCart);
    } else if (location.state !== null && location.state?.previous.includes("mini-track")) {
      setNextRoute("/" + ROUTES.MINI_TRACK.ReviewCart);
    } else if (location.state !== null && location.state?.previous.includes("virtual-reality")) {
      setNextRoute("/" + ROUTES.VIRTUAL_REALITY.ReviewCart);
    } else {
      setNextRoute("/" + ROUTES.MAIN_TRACK.ReviewCart);
    }
  }, [location.state, productRenderedLocation]);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let controller = new AbortController();
    setLoading(true);
    fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_PRODUCT_BY_HANDLE,
        variables: {
          handle: "pre-paid-arcade-cards",
        },
      }),
    })
      .then((e) => e.json())
      .then((e) => {
        let product: any = e?.data?.product || {};
        product.variants = product?.variants?.edges?.map((e: any) => e.node) || [];
        setProduct(product as IGProduct);
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setLoading(false);
      });
    return () => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    };
  }, []);

  const handleAddToCartClick = useCallback(async () => {
    try {
      setIsProductAdding(true);

      if (productRenderedLocation === "external") {
        const lines = {
          items: [
            {
              merchandiseId: selectedVariantId,
              quantity: selectedQuantity,
            },
          ],
        };

        const addToCart = await query(ADD_TO_CART_LINES, {
          cartId: headlessCartInfo.id,
          lines: lines.items,
        });

        const isAddedToCart = addToCart?.data?.cartLinesAdd;
        if (isAddedToCart) {
          const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
          dispatch({
            type: "SET_CARTITEMS_HEADLESS",
            payload: {
              headlessCart: [...cartItems] as ICartItemHeadless[],
            },
          });

          const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: cartTotalAmount,
            },
          });
          dispatch({
            type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
            payload: {
              isHeadlessMiniCartIsShowing: true,
            },
          });
        } else {
          console.error("isAddedToCart Cart data is missing in the GraphQL response.", isAddedToCart);
        }
      } else {
        let formData = {
          items: [
            {
              id: getAdminIdFromStoreFrontId(selectedVariantId),
              quantity: selectedQuantity,
            },
          ],
        };

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        const cart = await response.json();
        if (cart) {
          miniCartCallback(true);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsProductAdding(false);
    }
  }, [dispatch, productRenderedLocation, selectedQuantity, selectedVariantId, headlessCartInfo]);

  const handleBookNow = useCallback(async () => {
    try {
      setIsBookingNow(true);
      await handleAddToCartClick();

      if (productRenderedLocation === "external") {
        if (headlessCartInfo.checkoutUrl) {
          window.open(headlessCartInfo.checkoutUrl, "_blank");
        }
      } else {
        window.location.href = "/checkout";
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsBookingNow(false);
    }
  }, [headlessCartInfo, handleAddToCartClick, productRenderedLocation]);

  const handleArcadePlus = () => {
    dispatch({
      type: "SET_ARCADE_GAME_QUANTITY",
      payload: {
        selectedQuantity: selectedQuantity + 1,
      },
    });
  };

  const handleArcadeMinus = () => {
    dispatch({
      type: "SET_ARCADE_GAME_QUANTITY",
      payload: {
        selectedQuantity: Math.max(1, selectedQuantity - 1),
      },
    });
  };

  return (
    <>
      <ProgressBar maxValue={100} currentValue={70} />
      {productRenderedLocation === "external" && <MiniCart />}
      <div className="container">
        <div className="tw-block lg:tw-hidden">
          <span className="tw-block tw-text-[18px] lg:tw-text-[24px] tw-text-[#F609FF] tw-font-[700] tw-mt-[30px] tw-text-center">ARCADE GAME CARD</span>
          <div className="tw-text-white tw-text-center tw-text-[24px] tw-font-extrabold">Pre-Paid Arcade Card</div>
        </div>
        <div className="tw-hidden lg:tw-block">
          <Navigation
            previous={{
              active: true,
              to: "/" + ROUTES.MAIN_TRACK.BOOKING_SUMMARY,
            }}
            next={{ active: nextRoute !== "#" && !isProductOutOfStock, to: isProductOutOfStock ? "#" : nextRoute }}
            title={"Pre-Paid Arcade Card"}
            subtitle={"ARCADE GAME CARD"}
          />
        </div>
      </div>
      {loading ? (
        <>
          <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px] tw-mx-auto tw-w-full">
            <img src={LoadingAnimationURL} alt="" className="tw-w-[230px] md:tw-w-[300px]" />
          </div>
        </>
      ) : (
        <>
          <div className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"}`}>
            <div
              className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-[30px] lg:tw-gap-[50px] tw-my-[30px] lg:tw-mt-[40px] lg:tw-mb-[40px]"
              id="mcp-product-id-selector"
              data-product-id={product && getAdminIdFromStoreFrontId(product.variants[0].product.id as string)}
            >
              {product &&
                product.variants.map((variant: IGVariant) => {
                  return (
                    <ArcadeGame
                      key={variant.id}
                      variantId={variant.id}
                      title={variant.title as "10" | "20" | "50"}
                      price={Number(variant.priceV2.amount)}
                      isVariantOutOfStock={variant.quantityAvailable <= 0}
                    />
                  );
                })}
            </div>
            {isProductOutOfStock ? (
              <>
                <div className={`tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px]`}>
                  <span className={`tw-text-[red] tw-font-[700] tw-text-center`}>Sold Out</span>
                </div>
              </>
            ) : (
              <>
                <div className="tw-flex tw-justify-center tw-items-center">
                  <div className="tw-text-white tw-text-center">
                    <span className="tw-text-[20px] lg:tw-text-[25px] tw-font-[700]">Quantity</span>
                    <div className="tw-flex tw-justify-center tw-mt-[20px]">
                      <div className="tw-flex tw-items-center tw-gap-[30px]">
                        <span
                          className={`tw-rounded-full tw-p-[5px] hover:tw-cursor-pointer ${
                            selectedQuantity <= 1 ? "tw-bg-[#555758] hover:tw-cursor-not-allowed" : "tw-bg-[#9D62FE] hover:tw-cursor-pointer"
                          }`}
                          onClick={handleArcadeMinus}
                        >
                          <MinusIcon className="tw-w-[35px] tw-h-[35px]" />
                        </span>
                        <span className="tw-text-[30px] tw-font-bold tw-border-[2px] tw-rounded-[15px] tw-border-[#EBECEF] tw-bg-[#393B41] tw-py-[4px] tw-px-[60px]">
                          {selectedQuantity}
                        </span>
                        <span className={`tw-rounded-full tw-p-[5px] tw-bg-[#9D62FE] hover:tw-cursor-pointer`} onClick={handleArcadePlus}>
                          <PlusIcon className="tw-w-[35px] tw-h-[35px]" />
                        </span>
                      </div>
                    </div>
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[20px] tw-mt-[30px] lg:tw-mt-[50px]">
                      <button
                        onClick={(e) => handleAddToCartClick()}
                        className="tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white tw-py-[16px] tw-px-[40px] bg-gradient-2 tw-rounded-full tw-cursor-pointer"
                        id="add-to-cart-button"
                      >
                        {isProductAdding ? (
                          <>
                            <ImSpinner6 className="tw-h-[30px] tw-w-[30px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                            <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                          </>
                        ) : (
                          <>
                            <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                            <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Add to Cart</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={(e) => handleBookNow()}
                        className="tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                      >
                        {isBookingNow ? "Booking..." : "Book Now"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px]">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div className="tw-block lg:tw-hidden">
            <Navigation
              previous={{
                active: true,
                to: "/" + ROUTES.MAIN_TRACK.BOOKING_SUMMARY,
              }}
              next={{ active: nextRoute !== "#", to: nextRoute }}
              title={"Pre-Paid Arcade Card"}
              subtitle={"ARCADE GAME CARD"}
            />
          </div>
          <div
            className={`${
              productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"
            } tw-relative tw-flex tw-justify-end tw-items-center tw-gap-[15px] lg:tw-block`}
          >
            <NewsletterPopup />
          </div>
        </div>
      </div>
    </>
  );
};

export default ArcadeCard;
